import React from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import colors from 'src/style/colors';
import { mediaQueries } from 'src/style/breakpoints';

const formationsData = [
  {
    badge: 'Bootcamp',
    title: 'Formation Développeur Web & Mobile',
    highlight: '+ IA',
    infos: [
      { bold: '10 semaines', text: 'intensives' },
      { bold: '100% à distance' },
      { text: 'Titre RNCP', bold: 'niveau 6', textEnd: '(BAC+3/4)' },
    ],
  },
  {
    badge: 'E-learning',
    title: 'Formation Développeur Web (e-learning + mentoring)',
    highlight: null,
    infos: [
      { bold: '3 à 6 mois', text: 'à votre rythme' },
      { bold: '100% en ligne' },
      { text: 'Titre RNCP', bold: 'niveau 5', textEnd: '(BAC+2)' },
    ],
  },
  {
    badge: 'Alternance',
    title: 'Formation Développeur Web & Mobile',
    highlight: '+ IA',
    infos: [
      { bold: '18 mois', text: 'en alternance' },
      { bold: 'Entreprise', text: '+ école' },
      { text: 'Titre RNCP', bold: 'niveau 6', textEnd: '(BAC+3/4)' },
    ],
  },
];

const FormationCard = ({ formation, children }) => {
  return (
    <div
      css={{
        backgroundColor: colors.white,
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        border: '2px solid transparent',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
          borderColor: colors.purple,
        },
        '&:hover .formation-image-overlay': {
          opacity: 0.7,
        },
      }}
    >
      {/* Image container */}
      <div
        css={{
          height: 180,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '16px 16px 0 0',
        }}
      >
        {children}
        {/* Purple overlay on hover */}
        <div
          className="formation-image-overlay"
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${colors.purple} 0%, ${colors.purpleLight} 100%)`,
            opacity: 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Content */}
      <div css={{ padding: '1.25rem' }}>
        <h3
          css={{
            fontSize: '1.1rem',
            marginBottom: '0.75rem',
            color: colors.purpleDark,
            lineHeight: 1.3,
            fontWeight: 700,
          }}
        >
          {formation.title}{' '}
          {formation.highlight && <span css={{ color: colors.yellow }}>{formation.highlight}</span>}
        </h3>

        <ul
          css={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            fontSize: '0.85rem',
            color: colors.darkGrey,
          }}
        >
          {formation.infos.map((info, index) => (
            <li
              key={index}
              css={{
                padding: '0.3rem 0',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
                '&::before': {
                  content: '"✓"',
                  color: colors.purple,
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  flexShrink: 0,
                  marginTop: '0.1rem',
                },
              }}
            >
              <span>
                {info.text && !info.bold && `${info.text} `}
                {info.bold && (
                  <strong css={{ color: colors.purpleDark, fontWeight: 700 }}>{info.bold}</strong>
                )}
                {info.text && info.bold && ` ${info.text}`}
                {info.boldEnd && (
                  <>
                    {' '}
                    <strong css={{ color: colors.purpleDark, fontWeight: 700 }}>
                      {info.boldEnd}
                    </strong>
                  </>
                )}
                {info.textEnd && ` ${info.textEnd}`}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const FormationsSection = () => {
  return (
    <section
      css={{
        padding: '3rem 2rem',
        backgroundColor: colors.white,
        [mediaQueries.phoneOnly]: {
          padding: '2rem 1rem',
        },
      }}
    >
      <div
        css={{
          maxWidth: 1200,
          margin: '0 auto',
        }}
      >
        {/* Section Title */}
        <h2
          css={{
            textAlign: 'center',
            fontSize: '2.5rem',
            marginBottom: '1rem',
            color: colors.purpleDark,
            fontWeight: 700,
            [mediaQueries.phoneOnly]: {
              fontSize: '1.75rem',
            },
          }}
        >
          Trouvez la <span css={{ color: colors.purple }}>formation</span> qu'il vous faut
        </h2>

        {/* Subtitle */}
        <p
          css={{
            textAlign: 'center',
            fontSize: '1.25rem',
            color: colors.darkGrey,
            marginBottom: '2rem',
            maxWidth: 800,
            marginLeft: 'auto',
            marginRight: 'auto',
            [mediaQueries.phoneOnly]: {
              fontSize: '1rem',
            },
          }}
        >
          Nous proposons des programmes à temps plein, en alternance et en e-learning, avec un
          enseignement à distance.
        </p>

        {/* Formations */}
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1.5rem',
            '@media only screen and (max-width: 968px)': {
              gridTemplateColumns: '1fr',
            },
          }}
        >
          {/* Bootcamp */}
          <FormationCard formation={formationsData[0]}>
            <StaticImage
              src="../../../data/images/2023_students.jpg"
              alt="Formation Bootcamp"
              css={{ width: '100%', height: '100%' }}
              imgStyle={{ objectFit: 'cover' }}
            />
          </FormationCard>

          {/* E-learning */}
          <FormationCard formation={formationsData[1]}>
            <StaticImage
              src="../../../data/images/2023_studentsAndTeacher.jpg"
              alt="Formation E-learning"
              css={{ width: '100%', height: '100%' }}
              imgStyle={{ objectFit: 'cover' }}
            />
          </FormationCard>

          {/* Alternance */}
          <FormationCard formation={formationsData[2]}>
            <StaticImage
              src="../../../data/images/2023_studentsAndTeacher2.jpg"
              alt="Formation Alternance"
              css={{ width: '100%', height: '100%' }}
              imgStyle={{ objectFit: 'cover' }}
            />
          </FormationCard>
        </div>
      </div>
    </section>
  );
};

export default FormationsSection;
